import { useCallback, useEffect, useMemo, useState } from 'react';
import type { TablePaginationProps } from '@/components/ui/TablePagination';
import type { PageResponse } from '@/types/api';

export const DEFAULT_PAGE_SIZE = 20;

export type PaginationState = ReturnType<typeof usePagination>;

export type ClientPaginatedRowsResult<T> = {
  rows: T[];
  page: number;
  totalPages: number;
  totalElements: number;
  onPageChange: (page: number) => void;
  tablePaginationProps: Pick<
    TablePaginationProps,
    'page' | 'totalPages' | 'totalElements' | 'onPageChange'
  >;
};

/** Props for DataTable page controls (search optional via dataTableSearchProps). */
export function dataTablePageProps(pagination: PaginationState) {
  return {
    page: pagination.page,
    totalPages: pagination.totalPages,
    totalElements: pagination.totalElements,
    onPageChange: pagination.setPage,
  };
}

export function dataTableSearchProps(pagination: PaginationState) {
  return {
    search: pagination.search,
    onSearchChange: (value: string) => {
      pagination.setSearch(value);
      pagination.resetPage();
    },
  };
}

export function usePagination(initialSize = DEFAULT_PAGE_SIZE) {
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(initialSize);
  const [search, setSearch] = useState('');
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  const applyPageResponse = useCallback(<T>(response: PageResponse<T>) => {
    setTotalPages(response.totalPages);
    setTotalElements(response.totalElements);
  }, []);

  /** For APIs that return a full array — drives TablePagination from local count. */
  const applyClientCount = useCallback(
    (count: number) => {
      setTotalElements(count);
      setTotalPages(count === 0 ? 0 : Math.ceil(count / size));
    },
    [size],
  );

  const resetPage = useCallback(() => setPage(0), []);

  return useMemo(
    () => ({
      page,
      setPage,
      size,
      setSize,
      search,
      setSearch,
      totalPages,
      totalElements,
      applyPageResponse,
      applyClientCount,
      resetPage,
      params: { page, size, search: search || undefined },
    }),
    [page, size, search, totalPages, totalElements, applyPageResponse, applyClientCount, resetPage],
  );
}

/** Slice a client-held list for the current page. */
export function paginateClientList<T>(items: T[], page: number, size: number): T[] {
  const start = page * size;
  return items.slice(start, start + size);
}

/** Client-side paging for in-memory lists (custom tables, RecordDataTable, etc.). */
export function useClientPaginatedRows<T>(
  items: T[],
  pageSize = DEFAULT_PAGE_SIZE,
): ClientPaginatedRowsResult<T> {
  const pagination = usePagination(pageSize);

  useEffect(() => {
    pagination.applyClientCount(items.length);
  }, [items.length, pagination.applyClientCount, pageSize]);

  useEffect(() => {
    if (pagination.totalPages > 0 && pagination.page >= pagination.totalPages) {
      pagination.resetPage();
    }
  }, [items.length, pagination.page, pagination.totalPages, pagination.resetPage]);

  const rows = useMemo(
    () => paginateClientList(items, pagination.page, pagination.size),
    [items, pagination.page, pagination.size],
  );

  const tablePaginationProps = dataTablePageProps(pagination);

  return {
    rows,
    ...tablePaginationProps,
    tablePaginationProps,
  };
}
